import type { PlateElementProps } from 'platejs/react';
import { useEditorReadOnly } from 'platejs/react';
import React, { useCallback, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';

import { SIGNATURE_BLOCK_MARKER_ATTR } from './constants';
import type { PlateSignatureNode } from './plateEmailSerializer';
import { sanitizeSignatureHtml } from './signature-sanitize';

interface SignatureElementProps extends PlateElementProps {
  /**
   * Remove this signature block from the draft. Always provided by
   * PlateEmailEditor with a reference-stable callback — whether the control is
   * SHOWN is decided here from the live `useEditorReadOnly()`, not by the
   * presence of this prop, so a `readOnly` flip is honoured on the mounted
   * editor (its plugin array is captured at mount). Removal is recorded
   * downstream as `signature_disposition: 'user_removed'`
   * (see `reconcileSignatureDisposition`), so nothing re-adds the block.
   */
  onRemove?: (element: PlateSignatureNode) => void;
}

export const SignatureElement: React.FC<SignatureElementProps> = ({
  attributes,
  children,
  element,
  onRemove,
}) => {
  const isReadOnly = useEditorReadOnly();
  const node = element as unknown as PlateSignatureNode;
  const html = node.html || '';

  // Sanitised on render rather than on store: the node's html must reach the
  // serializer verbatim (see signature-sanitize.ts). Memoised because
  // sanitising parses the whole block, which is wasted work on every keystroke
  // elsewhere in the editor.
  const sanitizedHtml = useMemo(() => sanitizeSignatureHtml(html), [html]);

  const handleRemoveClick = useCallback(() => {
    onRemove?.(node);
  }, [onRemove, node]);

  return (
    // Render-time CSS anchor: scopes .email-content-baseline revert rules to
    // the signature subtree. Not persisted — the stored HTML also carries the
    // marker, but this wrapper is the fail-closed source of truth.
    <div
      {...attributes}
      {...{ [SIGNATURE_BLOCK_MARKER_ATTR]: 'true' }}
      className='group/signature relative'
    >
      <div
        contentEditable={false}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
      {onRemove && !isReadOnly && (
        // The block itself is an opaque void (contentEditable=false), so this
        // button is the ONLY affordance for deleting it. A labeled button —
        // not a bare ✕ — so hovering the signature says exactly what the
        // action does. On a fine (mouse) pointer it stays hidden AND
        // non-hit-testable (`pointer-events-none`) until hover/focus reveals
        // it, so it never sits on the signature while reading and a click on
        // the invisible corner can't remove the block. Touch devices have no
        // hover, so `pointer-coarse` makes it always visible and tappable —
        // otherwise the transparent control was hit-testable with no way to
        // see it, and a tap near the top-right could remove the signature.
        <span
          contentEditable={false}
          className='absolute top-1 right-1 rounded-lg bg-[var(--color-bg-primary)]/90 opacity-0 pointer-events-none transition-opacity focus-within:opacity-100 focus-within:pointer-events-auto group-hover/signature:opacity-100 group-hover/signature:pointer-events-auto pointer-coarse:opacity-100 pointer-coarse:pointer-events-auto'
        >
          <Button size='xs' color='tertiary' onClick={handleRemoveClick}>
            Remove signature
          </Button>
        </span>
      )}
      {children}
    </div>
  );
};
